import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { api, parseErrorDetail } from "../lib/api";

/**
 * OWNER RULING 2026 R11b — protected one-time SMTP secret entry.
 * OWNER_ADMIN only (server enforces via require_owner_admin).
 * The plaintext never leaves this component's local state; we clear it
 * immediately after a successful POST so it does not linger in React
 * DevTools / component memory. Never persisted to localStorage.
 */
export default function AdminSmtpSettings() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/admin/smtp-secret/status");
      setStatus(r.data);
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!password || saving) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const r = await api.post("/admin/smtp-secret", { password });
      // Clear plaintext IMMEDIATELY on success.
      setPassword("");
      setStatus((s) => ({
        ...(s || {}),
        is_set: true,
        length: r.data.length,
        set_at: r.data.set_at,
        non_secret_config: r.data.non_secret_config,
      }));
      setNotice(`Stored ${r.data.length}-char password at ${new Date(r.data.set_at).toLocaleString()}. Transport mode is still NOOP — the owner must flip EMAIL_TRANSPORT_MODE and restart the backend to enable real sending.`);
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setSaving(false);
    }
  }, [password, saving]);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState("");

  const onTestSend = useCallback(async () => {
    if (testing) return;
    setTesting(true); setTestResult(null); setTestError("");
    try {
      const r = await api.post("/admin/smtp-secret/test-send", {});
      setTestResult(r.data);
    } catch (err) {
      setTestError(parseErrorDetail(err));
    } finally {
      setTesting(false);
    }
  }, [testing]);

  const cfg = status?.non_secret_config || {};

  return (
    <AdminShell title="SMTP secret">
      <section className="apwo-panel" data-testid="admin-smtp-panel">
        <div className="apwo-summary-head">
          <h2>Gmail SMTP app password <span className="apwo-cell-muted">owner-only</span></h2>
          <button className="apwo-linky" onClick={() => nav("/admin")} data-testid="admin-smtp-back">← Admin</button>
        </div>

        <div className="apwo-info-panel" data-testid="admin-smtp-explainer">
          <span className="apwo-info-icon" aria-hidden>ⓘ</span>
          <div>
            <p className="apwo-info-title">One-time secret entry. Nothing is sent from this page.</p>
            <p className="apwo-info-legend">
              Paste the 16-character Gmail app password (with or without spaces —
              spaces will be stripped). The value is written to
              <code> /app/backend/.env</code> only; it is never returned by any
              API endpoint and never appears in logs, audit rows, or the git
              working tree. Transport mode stays <code>NOOP</code> until the
              owner explicitly flips it — this page cannot enable sending.
            </p>
          </div>
        </div>

        {loading && <p className="apwo-muted">Loading status…</p>}

        {!loading && (
          <>
            <div className="apwo-panel apwo-panel--totals" data-testid="admin-smtp-status">
              <h3 className="apwo-panel-heading">Current SMTP configuration</h3>
              <div className="apwo-totals-row">
                <span>SMTP password</span>
                <span data-testid="admin-smtp-set-line">
                  {status?.is_set
                    ? <><strong>Set</strong> ({status.length} chars){status.set_at && <> at <span data-testid="admin-smtp-set-at">{new Date(status.set_at).toLocaleString()}</span></>}</>
                    : <strong data-testid="admin-smtp-not-set">Not set</strong>}
                </span>
              </div>
              <div className="apwo-totals-row"><span>SMTP host</span><span data-testid="admin-smtp-host">{cfg.smtp_host || "—"}</span></div>
              <div className="apwo-totals-row"><span>SMTP port</span><span data-testid="admin-smtp-port">{cfg.smtp_port || "—"}</span></div>
              <div className="apwo-totals-row"><span>SMTP username</span><span data-testid="admin-smtp-username">{cfg.smtp_username || "—"}</span></div>
              <div className="apwo-totals-row"><span>STARTTLS</span><span>{cfg.smtp_starttls || "—"}</span></div>
              <div className="apwo-totals-row"><span>Owner recipient</span><span data-testid="admin-smtp-owner-email">{cfg.owner_notification_email || "—"}</span></div>
              <div className="apwo-totals-row"><span>From: header</span><span data-testid="admin-smtp-from">{cfg.owner_notification_from || "—"}</span></div>
              <div className="apwo-totals-row apwo-totals-row--total">
                <span>Transport mode</span>
                <strong data-testid="admin-smtp-mode">{cfg.email_transport_mode || "—"}</strong>
              </div>
              <div className="apwo-totals-row"><span>.env perms</span><span>{cfg.env_file_perms || "—"} (owner-only read)</span></div>
              <div className="apwo-totals-row"><span>.env git-ignored</span><span>{cfg.env_file_gitignored ? "yes" : "no"}</span></div>
            </div>

            <form className="apwo-panel apwo-panel--summary" onSubmit={onSubmit} noValidate autoComplete="off" data-testid="admin-smtp-form">
              <h3 className="apwo-panel-heading">
                {status?.is_set ? "Overwrite the app password" : "Set the app password"}
              </h3>
              <label className="apwo-field">
                <span>Gmail app password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  spellCheck="false"
                  autoCorrect="off"
                  autoCapitalize="off"
                  inputMode="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="paste the 16-char app password (spaces OK)"
                  data-testid="admin-smtp-password-input"
                  minLength={8}
                  maxLength={200}
                  required
                />
              </label>
              {error && <div className="apwo-alert-error" data-testid="admin-smtp-error">{error}</div>}
              {notice && <div className="apwo-alert-success" data-testid="admin-smtp-notice">{notice}</div>}
              <div className="apwo-empty-actions">
                <button
                  type="submit"
                  className="apwo-btn-primary apwo-btn-primary--submit"
                  disabled={!password || saving}
                  data-testid="admin-smtp-save"
                >
                  {saving ? "Storing…" : (status?.is_set ? "Overwrite password" : "Save password")}
                </button>
              </div>
              <p className="apwo-fineprint">
                The value is masked here, stripped of spaces server-side, and
                written to <code>/app/backend/.env</code> with mode 0600. No
                endpoint on this portal ever returns the value.
              </p>
            </form>

            {/* R22 — OWNER-only SMTP test-send. Recipient is server-locked to
                the configured OWNER_NOTIFICATION_EMAIL; response body carries
                only PASS/FAIL and a non-secret error category. */}
            <div className="apwo-panel apwo-panel--summary" data-testid="admin-smtp-test-panel">
              <h3 style={{ marginTop: 0 }}>Send test email to Owner</h3>
              <p className="apwo-muted" style={{ marginTop: 0 }}>
                Sends a single non-transactional email to the configured owner
                address (<span data-testid="admin-smtp-test-owner">{cfg.owner_notification_email || "—"}</span>)
                to prove the SMTP configuration works end-to-end. The recipient
                is fixed server-side — no address can be supplied from the
                browser. No order, quote, invoice, or reward record is created.
              </p>
              <div className="apwo-empty-actions" style={{ justifyContent: "flex-start" }}>
                <button
                  type="button"
                  className="apwo-btn-primary"
                  onClick={onTestSend}
                  disabled={testing}
                  data-testid="admin-smtp-test-send"
                >
                  {testing ? "Sending…" : "Send test email to Owner"}
                </button>
              </div>
              {testError && (
                <div className="apwo-alert-error" data-testid="admin-smtp-test-error" style={{ marginTop: 12 }}>
                  {testError}
                </div>
              )}
              {testResult && (
                <div
                  className={testResult.ok ? "apwo-alert-success" : "apwo-alert-error"}
                  data-testid="admin-smtp-test-result"
                  style={{ marginTop: 12 }}
                >
                  <strong data-testid="admin-smtp-test-verdict">
                    {testResult.ok ? "PASS" : "FAIL"}
                  </strong>{" "}
                  <span className="apwo-muted">
                    · transport <code data-testid="admin-smtp-test-transport">{testResult.transport || "—"}</code>{" "}
                    · category <code data-testid="admin-smtp-test-category">{testResult.category}</code>
                    {testResult.to_domain ? <> · owner domain <code>{testResult.to_domain}</code></> : null}
                  </span>
                </div>
              )}
              <p className="apwo-fineprint" data-testid="admin-smtp-test-fineprint">
                Rate-limited per owner. Result never returns the SMTP password
                or full server-auth strings — only a non-secret category such
                as <code>sent</code>, <code>auth_rejected</code>, or
                <code> connection_failed</code>.
              </p>
            </div>
          </>
        )}
      </section>
    </AdminShell>
  );
}
